import { type CSSProperties, type ReactNode } from 'react';
import clsx from 'clsx';
import { type LoginAppearanceDto } from '@/app/models/LoginAppearanceDto';
import { LoginLayoutType } from '@/app/models/LoginLayoutType';
import { LoginBackgroundType } from '@/app/models/LoginBackgroundType';
import { LoginThemeType } from '@/app/models/LoginThemeType';
import { SplashScreenType } from '@/app/models/SplashScreenType';
import { getInterfaceColorTokens } from '@/app/modules/interface-appearance/interfaceColors';
import { SplashScreenMedia } from '@/app/modules/interface-appearance/SplashScreenMedia';
import SafeImage from '@/app/modules/widgets/SafeImage';
import { LoginBrand } from './LoginBrand';
import { useLoginTheme } from './useLoginTheme';
import { getDefaultLoginTexts, getLoginMediaSettings } from './core/_utils';
import './LoginAppearanceFrame.css';

type LoginAppearanceFrameProps = {
  appearance: LoginAppearanceDto;
  children: ReactNode;
  showHeading?: boolean;
  className?: string;
};

export const LoginAppearanceFrame = ({
  appearance,
  children,
  showHeading = true,
  className,
}: LoginAppearanceFrameProps) => {
  const layout = appearance.layout?.value ?? LoginLayoutType.SPLIT;
  const theme = appearance.theme?.value ?? LoginThemeType.LIGHT;
  const resolvedTheme = useLoginTheme(theme);
  const isDark = resolvedTheme === 'dark';
  const backgroundType = appearance.background_type?.value ?? LoginBackgroundType.AUTO;
  const backgroundMediaSource = appearance.background_media_url || undefined;
  const { hasImage, hasAnimation, backgroundSource } = getLoginMediaSettings(
    layout,
    backgroundType,
    backgroundMediaSource,
  );
  const colorTokens = getInterfaceColorTokens(appearance.primary_color, appearance.panel_color);
  const defaultBackground = isDark ? '#15171c' : '#ffffff';
  const overlayPercent = appearance.background_overlay_percent ?? 35;
  const style = {
    ...colorTokens,
    '--app-primary-text': isDark ? colorTokens['--app-primary-text-dark'] : colorTokens['--app-primary-text-light'],
    '--login-background': appearance.background_color || defaultBackground,
    '--login-panel': appearance.panel_color || '#0d0e12',
    '--login-panel-text': colorTokens['--app-sidebar-foreground'],
    '--login-overlay': Math.min(80, Math.max(0, overlayPercent)) / 100,
  } as CSSProperties;
  const systemName = appearance.system_name || 'EasyPDV';
  const defaultTexts = getDefaultLoginTexts();
  const title = appearance.title ?? defaultTexts.title;
  const message = appearance.message ?? defaultTexts.message;
  const footer = appearance.footer ?? defaultTexts.footer;
  const supportLabel = appearance.support_label ?? defaultTexts.support_label;
  const supportUrl = appearance.support_url?.trim();
  const hasSupportLink = supportUrl && /^https?:\/\//i.test(supportUrl) && supportLabel;
  const layoutClassName = `login-layout-${layout.toLowerCase()}`;
  const hasBrandPanel = layout !== LoginLayoutType.ESSENTIAL;
  const brand = <LoginBrand logoUrl={appearance.logo_url} systemName={systemName} />;
  const media = (
    <div aria-hidden="true" className="login-backdrop">
      {hasImage ? (
        <SafeImage
          alt=""
          className="login-background-image"
          fallbackSrc="/media/logos/login_page_image.png"
          src={backgroundSource}
        />
      ) : null}
      {hasAnimation ? <SplashScreenMedia source={backgroundMediaSource} type={SplashScreenType.LOTTIE} /> : null}
      {hasImage || hasAnimation ? <div className="login-background-overlay" /> : null}
    </div>
  );

  return (
    <section className={clsx('login-appearance', className)} data-bs-theme={resolvedTheme} style={style}>
      <div className={clsx('login-layout', layoutClassName, { 'login-has-media': hasImage || hasAnimation })}>
        {layout === LoginLayoutType.SPLIT ? null : media}
        {hasBrandPanel ? (
          <div className="login-brand-panel">
            {layout === LoginLayoutType.SPLIT ? media : null}
            <div className="login-brand">{brand}</div>
            <div className="login-brand-caption">
              <div className="login-brand-title">{systemName}</div>
              <p>Seu negócio, sempre em movimento.</p>
            </div>
          </div>
        ) : null}
        <div className="login-form-pane">
          <div className="login-form-card">
            <div className="login-form-brand">{brand}</div>
            {showHeading ? (
              <div className="login-heading">
                <h1 className={clsx({ 'visually-hidden': !title })}>{title || defaultTexts.title}</h1>
                {message ? <p>{message}</p> : null}
              </div>
            ) : null}
            {children}
            {hasSupportLink ? (
              <a className="login-support" href={supportUrl} rel="noopener noreferrer" target="_blank">
                {supportLabel}
              </a>
            ) : null}
          </div>
          {footer ? <div className="login-footer">{footer}</div> : null}
        </div>
      </div>
    </section>
  );
};
